import { ListDocumentsRequest, photoTypeRegex, videoTypeRegex } from '@eigtech/documents-types'
import { JobId, isJobCorn } from '@eigtech/restoration-types'
import { getResourceId } from '@eigtech/shared-corn'
import { isClaimCorn } from '@eigtech/shared-corn-helper'
import {
  Badge,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
  chakra,
} from '@eigtech/ui-shared-dave'
import {
  AugmentedDocument,
  DocumentV2WithTags,
  UseOrganizeDocumentsProps,
  getDocumentDescription,
  getDocumentTitle,
  useBaseOrganizeDocuments,
  useGetDocumentUrl,
  useGetEntityDocumentsOptionallyWithTags,
} from '@eigtech/ui-shared-documents'
import { FieldValues, ReactSelectField, ReactSelectFieldProps } from '@eigtech/ui-shared-forms'
import { LinkIconButton, useRoutesContext } from '@eigtech/ui-shared-router'
import { TagItem } from '@eigtech/ui-shared-tags-web'
import { GroupBase, SelectComponentsConfig, chakraComponents } from 'chakra-react-select'
import { last } from 'lodash-es'
import { useMemo } from 'react'
import { MdRefresh, MdUploadFile } from 'react-icons/md'

export type DocumentSelectFieldProps<TFieldValues extends FieldValues = FieldValues> =
  ListDocumentsRequest &
    Pick<UseOrganizeDocumentsProps, 'filterFn'> &
    ReactSelectFieldProps<TFieldValues, DocumentOption, false, GroupBase<DocumentOption>> & {
      withDocumentTags?: boolean
      sortFn?: (
        a: { label: string; value: string; document: DocumentV2WithTags },
        b: { label: string; value: string; document: DocumentV2WithTags }
      ) => number
    }

export function DocumentSelectField<TFieldValues extends FieldValues = FieldValues>({
  filterFn,
  name,
  entityId: entityCorn,
  cohort,
  sortFn,
  withDocumentTags,
  ...props
}: DocumentSelectFieldProps<TFieldValues>) {
  const {
    data,
    isLoading,
    refetch: refetchDocuments,
  } = useGetEntityDocumentsOptionallyWithTags({
    entityId: entityCorn,
    cohort,
    withTags: withDocumentTags,
  })

  const documents = useBaseOrganizeDocuments({ documents: data, filterFn })
  const documentOptions = useMemo(() => {
    const mapped = documents.map((document) => ({
      label: getDocumentTitle(document),
      value: document.id,
      document,
    }))

    if (sortFn) {
      mapped.sort(sortFn)
    }

    return mapped
  }, [documents, sortFn])

  const entityId = getResourceId(entityCorn)

  const { jobDetailUploadDocument, claimDetailUploadDocument } = useRoutesContext()

  return (
    <HStack alignItems="flex-start">
      <ReactSelectField<TFieldValues, DocumentOption, false, GroupBase<DocumentOption>>
        components={selectComponents}
        isLoading={isLoading}
        label="Document"
        name={name}
        options={documentOptions}
        {...props}
      />

      <ButtonGroup pt="8" size="md" variant="outline">
        <Tooltip label="Refetch documents">
          <IconButton
            aria-label="Refetch documents"
            icon={<Icon as={MdRefresh} />}
            onClick={() => refetchDocuments()}
          />
        </Tooltip>

        {isJobCorn(entityCorn) && jobDetailUploadDocument && (
          <Tooltip label="Upload Documents">
            <LinkIconButton
              aria-label="Upload Documents"
              icon={<Icon as={MdUploadFile} />}
              target={`upload_${entityId}`}
              to={jobDetailUploadDocument(entityId as JobId)}
            />
          </Tooltip>
        )}

        {isClaimCorn(entityCorn) && claimDetailUploadDocument && (
          <Tooltip label="Upload Documents">
            <LinkIconButton
              aria-label="Upload Documents"
              icon={<Icon as={MdUploadFile} />}
              target={`upload_${entityId}`}
              to={claimDetailUploadDocument(entityId)}
            />
          </Tooltip>
        )}
      </ButtonGroup>
    </HStack>
  )
}

const selectComponents: SelectComponentsConfig<DocumentOption, false, GroupBase<DocumentOption>> = {
  Option: ({ children: __, ...props }) => {
    const { filename, id, entityId, tags } = props.data.document

    const fileExtension = last(filename.split('.'))

    const { data: url } = useGetDocumentUrl({ entityId, documentId: id })

    return (
      <chakraComponents.Option {...props}>
        <HStack>
          {filename.match(videoTypeRegex) ? (
            <chakra.video autoPlay maxH="44" maxW="44" muted src={url} />
          ) : filename.match(photoTypeRegex) ? (
            <Image fallback={<Skeleton h="20" w="20" />} maxH="44" maxW="44" src={url} />
          ) : (
            <Flex justifyContent="flex-end">
              <Badge>{fileExtension?.toLocaleUpperCase() ?? '(Unknown)'}</Badge>
            </Flex>
          )}

          <Stack spacing="1">
            <Stack spacing="0.5">
              <Text>{props.data.label}</Text>
              <Text fontSize="xs" noOfLines={2}>
                {getDocumentDescription(props.data.document)}
              </Text>
            </Stack>

            {tags?.length && (
              <Wrap spacing="2">
                {tags.map((tag) => (
                  <WrapItem key={`${tag.key}:${tag.value}`}>
                    <TagItem size="sm" tag={tag} />
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Stack>
        </HStack>
      </chakraComponents.Option>
    )
  },
}

type DocumentOption = {
  label: string
  value: string
  document: AugmentedDocument
}
